import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Field } from './Field';

export type AsyncSelectOption = {
    id: number | string;
    label: string;
    description?: string;
    [key: string]: unknown;
};

export type PaginatedResponse<T = AsyncSelectOption> = {
    data: T[];
    current_page?: number;
    last_page?: number;
    has_more?: boolean;
    total?: number;
};

export type AsyncSearchSelectProps<T extends AsyncSelectOption = AsyncSelectOption> = {
    id: string;
    label?: string;
    value: number | string | null | undefined;
    initialLabel?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    fetchUrl?: string;
    loadOptions?: (query: string, page: number) => Promise<PaginatedResponse<T> | T[]>;
    onChange: (value: number | string | '', option: T | null) => void;
    error?: string | boolean;
    disabled?: boolean;
    required?: boolean;
    clearable?: boolean;
    minChars?: number;
    dropdownPlacement?: 'below' | 'above';
    renderOption?: (option: T, isSelected: boolean) => ReactNode;
    formatLabel?: (option: T) => string;
    className?: string;
};

export function AsyncSearchSelect<T extends AsyncSelectOption = AsyncSelectOption>({
    id,
    label,
    value,
    initialLabel = '',
    placeholder = 'Selecione…',
    searchPlaceholder = 'Digite para pesquisar…',
    fetchUrl,
    loadOptions,
    onChange,
    error,
    disabled = false,
    clearable = true,
    minChars = 0,
    dropdownPlacement = 'below',
    renderOption,
    formatLabel = (opt) => opt.label,
    className = '',
}: AsyncSearchSelectProps<T>) {
    const [selectedLabel, setSelectedLabel] = useState<string>(initialLabel);
    const [query, setQuery] = useState<string>('');
    const [results, setResults] = useState<T[]>([]);
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [highlightIndex, setHighlightIndex] = useState<number>(-1);
    const [coords, setCoords] = useState<{
        top: number;
        bottom: number;
        left: number;
        width: number;
        maxHeight: number;
        placement: 'below' | 'above';
    }>({ top: 0, bottom: 0, left: 0, width: 280, maxHeight: 240, placement: 'below' });

    const containerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const debounceRef = useRef<number | null>(null);

    const isSelected = value !== null && value !== undefined && value !== '';

    const updateCoords = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const width = Math.max(rect.width, 320);
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));

        let placement: 'below' | 'above' = dropdownPlacement;
        if (dropdownPlacement === 'below' && spaceBelow < 150 && spaceAbove > spaceBelow + 50) {
            placement = 'above';
        } else if (dropdownPlacement === 'above' && spaceAbove < 150 && spaceBelow > spaceAbove + 50) {
            placement = 'below';
        }

        const maxHeight = placement === 'above'
            ? Math.min(300, Math.max(120, spaceAbove - 16))
            : Math.min(300, Math.max(120, spaceBelow - 16));

        setCoords({
            top: rect.bottom + 4,
            bottom: window.innerHeight - rect.top + 4,
            left,
            width,
            maxHeight,
            placement,
        });
    }, [dropdownPlacement]);

    useEffect(() => {
        if (!open) return;
        updateCoords();

        function handleScrollOrResize() {
            updateCoords();
        }

        window.addEventListener('resize', handleScrollOrResize);
        window.addEventListener('scroll', handleScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', handleScrollOrResize);
            window.removeEventListener('scroll', handleScrollOrResize, true);
        };
    }, [open, updateCoords]);

    useEffect(() => {
        if (initialLabel) {
            setSelectedLabel(initialLabel);
        }
    }, [initialLabel]);

    useEffect(() => {
        if (!isSelected && !initialLabel) {
            setSelectedLabel('');
        }
    }, [value, isSelected, initialLabel]);

    const performFetch = useCallback(
        async (searchQuery: string, pageNumber: number): Promise<PaginatedResponse<T>> => {
            if (loadOptions) {
                const res = await loadOptions(searchQuery, pageNumber);
                if (Array.isArray(res)) {
                    return { data: res, has_more: false };
                }
                return res;
            }

            if (!fetchUrl) {
                return { data: [], has_more: false };
            }

            const url = new URL(fetchUrl, window.location.origin);
            if (searchQuery.trim()) {
                url.searchParams.set('q', searchQuery.trim());
                url.searchParams.set('pesquisa', searchQuery.trim());
            }
            url.searchParams.set('page', String(pageNumber));
            url.searchParams.set('limit', '20');

            const response = await fetch(url.toString(), {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar opções.');
            }

            const json = await response.json();
            if (Array.isArray(json)) {
                return { data: json as T[], has_more: false };
            }
            return {
                data: (json.data ?? []) as T[],
                has_more: Boolean(json.has_more ?? (json.current_page && json.last_page && json.current_page < json.last_page)),
                current_page: json.current_page,
                last_page: json.last_page,
                total: json.total,
            };
        },
        [fetchUrl, loadOptions],
    );

    // Initial search or query change
    useEffect(() => {
        if (!open) return;

        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
        }

        const trimmed = query.trim();
        if (minChars > 0 && trimmed.length < minChars && trimmed.length > 0) {
            setResults([]);
            setHasMore(false);
            return;
        }

        debounceRef.current = window.setTimeout(async () => {
            setLoading(true);
            setPage(1);
            try {
                const res = await performFetch(trimmed, 1);
                setResults(res.data);
                setHasMore(Boolean(res.has_more));
                setHighlightIndex(res.data.length > 0 ? 0 : -1);
            } catch {
                setResults([]);
                setHasMore(false);
            } finally {
                setLoading(false);
            }
        }, 220);

        return () => {
            if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
            }
        };
    }, [open, query, minChars, performFetch]);

    // Close on click outside
    useEffect(() => {
        function onDocClick(event: MouseEvent) {
            const target = event.target as Node;
            if (
                containerRef.current?.contains(target) ||
                dropdownRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // Autofocus input on open
    useEffect(() => {
        if (open) {
            setQuery('');
            window.setTimeout(() => searchInputRef.current?.focus(), 40);
        }
    }, [open]);

    const handleLoadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        const nextPage = page + 1;
        try {
            const res = await performFetch(query.trim(), nextPage);
            setResults((prev) => [...prev, ...res.data]);
            setPage(nextPage);
            setHasMore(Boolean(res.has_more));
        } catch {
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleSelectOption = (option: T) => {
        const lbl = formatLabel(option);
        setSelectedLabel(lbl);
        onChange(option.id, option);
        setOpen(false);
    };

    const handleClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedLabel('');
        onChange('', null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!open) {
            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setOpen(true);
            }
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
            return;
        }

        if (event.key === 'Enter' && highlightIndex >= 0 && results[highlightIndex]) {
            event.preventDefault();
            handleSelectOption(results[highlightIndex]);
        }
    };

    const isErrorMessage = typeof error === 'string' && error.trim() !== '';

    const content = (
        <div
            ref={containerRef}
            className={`nx-cidade-autocomplete nx-w-full ${open ? 'is-open' : ''} ${!label ? className : ''}`}
            onKeyDown={handleKeyDown}
        >
            <button
                id={id}
                type="button"
                className={`nx-input nx-flex nx-items-center nx-justify-between nx-gap-2 nx-text-left ${
                    isSelected ? 'nx-cidade-input-wrap is-selected' : ''
                } ${error ? 'nx-input--error' : ''}`}
                onClick={() => !disabled && setOpen((prev) => !prev)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span
                    className={`nx-truncate ${
                        isSelected ? 'nx-text-body nx-font-medium' : 'nx-text-muted'
                    }`}
                >
                    {isSelected ? selectedLabel || String(value) : placeholder}
                </span>

                <div className="nx-flex nx-items-center nx-gap-1 nx-shrink-0">
                    {clearable && isSelected && !disabled ? (
                        <span
                            role="button"
                            tabIndex={0}
                            className="nx-p-1 nx-rounded hover:nx-bg-surface-hover nx-text-muted hover:nx-text-body"
                            onClick={handleClear}
                            title="Limpar seleção"
                        >
                            <X size={14} />
                        </span>
                    ) : null}
                    <ChevronDown size={15} className="nx-text-muted" />
                </div>
            </button>

            {open && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          ref={dropdownRef}
                          className={`nx-cidade-results ${
                              coords.placement === 'above' ? 'nx-cidade-results--above' : ''
                          } p-1.5 shadow-2xl`}
                          style={{
                              position: 'fixed',
                              zIndex: 999999,
                              pointerEvents: 'auto',
                              top: coords.placement === 'above' ? 'auto' : `${coords.top}px`,
                              bottom: coords.placement === 'above' ? `${coords.bottom}px` : 'auto',
                              left: `${coords.left}px`,
                              width: `${coords.width}px`,
                              maxWidth: 'min(460px, calc(100vw - 16px))',
                              maxHeight: `${coords.maxHeight}px`,
                              boxSizing: 'border-box',
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onKeyDown={handleKeyDown}
                      >
                          <div
                              className="nx-search-field nx-search-field--sm"
                              style={{
                                  marginBottom: '0.4rem',
                                  width: '100%',
                                  boxSizing: 'border-box',
                              }}
                          >
                              <Search
                                  size={14}
                                  className="nx-shrink-0"
                                  style={{ color: 'var(--nx-muted)' }}
                                  aria-hidden
                              />
                              <input
                                  ref={searchInputRef}
                                  type="search"
                                  value={query}
                                  onChange={(e) => setQuery(e.target.value)}
                                  placeholder={searchPlaceholder}
                                  autoComplete="off"
                                  style={{
                                      width: '100%',
                                      border: 0,
                                      outline: 'none',
                                      background: 'transparent',
                                      fontSize: '0.8125rem',
                                      color: 'var(--nx-text-body)',
                                  }}
                              />
                          </div>

                          <ul
                              ref={listRef}
                              role="listbox"
                              style={{
                                  maxHeight: `${Math.max(100, coords.maxHeight - 48)}px`,
                                  listStyle: 'none',
                                  margin: 0,
                                  padding: 0,
                              }}
                              className="overflow-y-auto overflow-x-hidden flex flex-col gap-0.5 p-0 m-0 list-none"
                          >
                              {loading ? (
                                  <li style={{ listStyle: 'none' }} className="nx-cidade-empty nx-flex nx-items-center nx-justify-center nx-gap-2 nx-py-2.5 nx-text-xs nx-text-muted list-none">
                                      <Loader2 size={13} className="nx-animate-spin" />
                                      <span>Buscando…</span>
                                  </li>
                              ) : results.length === 0 ? (
                                  <li style={{ listStyle: 'none' }} className="nx-cidade-empty nx-py-2.5 nx-text-xs nx-text-center nx-text-muted list-none">
                                      {minChars > 0 && query.trim().length < minChars
                                          ? `Digite ao menos ${minChars} caracteres para buscar.`
                                          : 'Nenhum resultado encontrado.'}
                                  </li>
                              ) : (
                                  <>
                                      {results.map((option, index) => {
                                          const selected = String(option.id) === String(value);
                                          const highlighted = index === highlightIndex;

                                          return (
                                              <li key={String(option.id)} style={{ listStyle: 'none' }} className="nx-min-w-0 nx-w-full list-none">
                                                  <button
                                                      type="button"
                                                      role="option"
                                                      aria-selected={selected}
                                                      className={`nx-cidade-option nx-flex nx-items-center nx-justify-between nx-w-full nx-min-w-0 nx-text-xs nx-py-1 nx-px-1.5 nx-rounded ${
                                                          highlighted ? 'is-highlighted' : ''
                                                      } ${selected ? 'nx-bg-primary-subtle nx-font-medium' : ''}`}
                                                      onMouseDown={(e) => {
                                                          e.preventDefault();
                                                          handleSelectOption(option);
                                                      }}
                                                      onClick={() => handleSelectOption(option)}
                                                      onMouseEnter={() => setHighlightIndex(index)}
                                                  >
                                                      <div className="nx-flex nx-flex-col nx-text-left nx-min-w-0 nx-flex-1 nx-pr-1">
                                                          {renderOption ? (
                                                              renderOption(option, selected)
                                                          ) : (
                                                              <>
                                                                  <span className="nx-truncate nx-font-medium">
                                                                      {formatLabel(option)}
                                                                  </span>
                                                                  {option.description ? (
                                                                      <span className="nx-text-[11px] nx-text-muted nx-truncate">
                                                                          {option.description}
                                                                      </span>
                                                                  ) : null}
                                                              </>
                                                          )}
                                                      </div>
                                                      {selected ? (
                                                          <Check
                                                              size={13}
                                                              className="nx-text-ok nx-shrink-0 nx-ml-1"
                                                          />
                                                      ) : null}
                                                  </button>
                                              </li>
                                          );
                                      })}

                                      {hasMore ? (
                                          <li className="nx-pt-1">
                                              <button
                                                  type="button"
                                                  onClick={handleLoadMore}
                                                  disabled={loadingMore}
                                                  className="nx-btn nx-btn--ghost nx-btn--sm nx-w-full nx-text-[11px] nx-py-1 nx-text-muted hover:nx-text-body"
                                              >
                                                  {loadingMore ? (
                                                      <Loader2 size={11} className="nx-animate-spin" />
                                                  ) : null}
                                                  Carregar mais resultados…
                                              </button>
                                          </li>
                                      ) : null}
                                  </>
                              )}
                          </ul>
                      </div>,
                      document.body,
                  )
                : null}
        </div>
    );

    if (label) {
        return (
            <Field
                label={label}
                htmlFor={id}
                error={isErrorMessage ? (error as string) : undefined}
                className={className}
            >
                {content}
            </Field>
        );
    }

    return content;
}
